import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { House, Buildings, Users, TrendUp, Tag, Storefront, ChartBar, EnvelopeSimple } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPE_LABELS = {
    apartment: 'Wohnungen',
    house: 'Häuser',
    land: 'Grundstücke',
    office: 'Büros',
    retail: 'Einzelhandel',
    warehouse: 'Lager/Hallen',
    other: 'Sonstige'
};

const MARKETING_TYPE_LABELS = {
    sale: 'Kaufen',
    rent: 'Mieten'
};

export default function DashboardPage() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const { data } = await axios.get(`${API}/api/dashboard/stats`, { withCredentials: true });
                setStats(data);
            } catch (error) {
                console.error('Error fetching stats:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    if (loading) {
        return (
            <div className="space-y-6" data-testid="dashboard-loading">
                <Skeleton className="h-10 w-64" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </div>
            </div>
        );
    }

    const statCards = [
        { 
            title: 'Gesamt Immobilien', 
            value: stats?.total_properties || 0, 
            icon: House, 
            link: '/properties',
            color: 'text-primary'
        },
        { 
            title: 'Aktive Immobilien', 
            value: stats?.active_properties || 0, 
            icon: TrendUp,
            link: '/properties',
            color: 'text-green-600'
        },
        { 
            title: 'Makler', 
            value: stats?.total_brokers || 0, 
            icon: Buildings,
            link: '/brokers',
            color: 'text-blue-600'
        },
        { 
            title: 'Benutzer', 
            value: stats?.total_users || 0, 
            icon: Users,
            link: '/users',
            color: 'text-purple-600'
        },
        { 
            title: 'Anfragen', 
            value: stats?.new_inquiries || 0, 
            icon: EnvelopeSimple,
            link: '/inquiries',
            color: 'text-orange-600',
            subtitle: stats?.total_inquiries ? `${stats.total_inquiries} gesamt` : null
        },
    ];

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                    Dashboard
                </h1>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 stagger-children">
                {statCards.map((stat) => {
                    const Icon = stat.icon;
                    return (
                        <Link to={stat.link} key={stat.title}>
                            <Card className="border hover:border-primary/50 transition-colors card-hover cursor-pointer" data-testid={`stat-${stat.title.toLowerCase().replace(/\s/g, '-')}`}>
                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                    <CardTitle className="text-sm font-medium text-muted-foreground">
                                        {stat.title}
                                    </CardTitle>
                                    <Icon size={20} className={stat.color} weight="fill" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-3xl font-bold" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                        {stat.value}
                                    </div>
                                </CardContent>
                            </Card>
                        </Link>
                    );
                })}
            </div>

            {/* Property Types, Marketing Types & Broker Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Property Types */}
                <Card className="border" data-testid="property-types-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                            <Tag size={20} weight="fill" />
                            Nach Immobilientyp
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {Object.entries(stats?.property_types || {}).length > 0 ? (
                                Object.entries(stats.property_types).map(([type, count]) => (
                                    <div key={type} className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            {PROPERTY_TYPE_LABELS[type] || type}
                                        </span>
                                        <span className="font-medium">{count}</span>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-muted-foreground">Keine Immobilien vorhanden</p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Marketing Types */}
                <Card className="border" data-testid="marketing-types-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                            <Storefront size={20} weight="fill" />
                            Nach Vermarktungsart
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {Object.entries(stats?.marketing_types || {}).length > 0 ? (
                                Object.entries(stats.marketing_types).map(([type, count]) => (
                                    <div key={type} className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            {MARKETING_TYPE_LABELS[type] || type}
                                        </span>
                                        <span className="font-medium">{count}</span>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-muted-foreground">Keine Immobilien vorhanden</p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Broker Stats */}
                <Card className="border" data-testid="broker-stats-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                            <ChartBar size={20} weight="fill" />
                            Nach Makler
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {stats?.broker_stats && stats.broker_stats.length > 0 ? (
                                stats.broker_stats.map((broker, idx) => (
                                    <div key={idx} className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground truncate mr-2">
                                            {broker.name}
                                        </span>
                                        <span className="font-medium shrink-0">{broker.count}</span>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-muted-foreground">Keine Makler-Daten vorhanden</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Quick Actions */}
            <Card className="border" data-testid="quick-actions-card">
                <CardHeader>
                    <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                        Schnellaktionen
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Link 
                            to="/import" 
                            className="flex items-center gap-3 p-4 border rounded-sm hover:bg-muted transition-colors"
                            data-testid="quick-action-import"
                        >
                            <div className="p-2 bg-primary/10 rounded-sm">
                                <House size={20} className="text-primary" />
                            </div>
                            <div>
                                <p className="font-medium">OpenImmo Import</p>
                                <p className="text-sm text-muted-foreground">ZIP-Datei hochladen</p>
                            </div>
                        </Link>
                        <Link 
                            to="/brokers" 
                            className="flex items-center gap-3 p-4 border rounded-sm hover:bg-muted transition-colors"
                            data-testid="quick-action-brokers"
                        >
                            <div className="p-2 bg-blue-100 rounded-sm">
                                <Buildings size={20} className="text-blue-600" />
                            </div>
                            <div>
                                <p className="font-medium">Makler verwalten</p>
                                <p className="text-sm text-muted-foreground">Makler hinzufügen</p>
                            </div>
                        </Link>
                        <Link 
                            to="/embed" 
                            className="flex items-center gap-3 p-4 border rounded-sm hover:bg-muted transition-colors"
                            data-testid="quick-action-embed"
                        >
                            <div className="p-2 bg-purple-100 rounded-sm">
                                <Storefront size={20} className="text-purple-600" />
                            </div>
                            <div>
                                <p className="font-medium">Embed Code</p>
                                <p className="text-sm text-muted-foreground">Widget generieren</p>
                            </div>
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
